import { useCallback, useState } from 'react'
import { DATE_RANGES, type DateRange } from '@shared/ranking'
import { track } from '../../lib/analytics'
import type { BrowseType } from './browseType'

export type ListSort = 'relevance' | 'date'
export const LIST_SORTS: readonly ListSort[] = ['relevance', 'date']

export interface ListControls {
  range: DateRange
  sort: ListSort
}

// Discover opens on the next 30 days, most relevant first, and the viewer
// widens or narrows it. One setting for every type, so switching the type chip
// doesn't also change the range.
const DEFAULTS: ListControls = { range: 'month', sort: 'relevance' }

// sessionStorage, not localStorage: the choice survives opening an event and
// coming back, but a fresh app launch starts from the defaults — a forgotten
// "Today" filter shouldn't make the page look empty days later.
const STORAGE_KEY = 'beacon_discover_list'

function read(): ListControls {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) {
      const v = JSON.parse(raw) as Partial<ListControls>
      if (DATE_RANGES.includes(v.range as DateRange) && LIST_SORTS.includes(v.sort as ListSort)) {
        return { range: v.range as DateRange, sort: v.sort as ListSort }
      }
    }
  } catch {
    // Storage blocked or corrupt — fall back to the defaults.
  }
  return DEFAULTS
}

/** `eventType` is only for analytics; the range and sort are shared across types. */
export function useListControls(eventType: BrowseType) {
  const [controls, setControls] = useState<ListControls>(read)
  const update = useCallback(
    (next: Partial<ListControls>) => {
      const merged = { ...controls, ...next }
      setControls(merged)
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
      } catch {
        // Not persisted this session; the in-memory choice still applies.
      }
      track('browse_list_changed', { event_type: eventType, range: merged.range, sort: merged.sort })
    },
    [controls, eventType],
  )
  return { range: controls.range, sort: controls.sort, update }
}
